(function (global) {
  function normalizeColor(value) {
    return String(value || '')
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');
  }

  function findComponentVariant(variants, selection, optionName, fixedOptions = {}) {
    const key = normalizeColor(selection);
    if (!key || !Array.isArray(variants)) return null;

    if (!optionName) {
      return variants.find((variant) => normalizeColor(variant.title) === key) || null;
    }

    const optionKey = normalizeColor(optionName);
    const requiredOptions = Object.entries(fixedOptions).filter(
      ([name, value]) => normalizeColor(name) && normalizeColor(value),
    );

    return (
      variants.find((variant) => {
        if (!Array.isArray(variant.options)) return false;
        const selectedOption = variant.options.find(
          (option) => normalizeColor(option.name) === optionKey,
        );
        if (!selectedOption || normalizeColor(selectedOption.value) !== key) return false;

        return requiredOptions.every(([requiredName, requiredValue]) => {
          const requiredKey = normalizeColor(requiredName);
          const matchingOption = variant.options.find(
            (option) => normalizeColor(option.name) === requiredKey,
          );
          return (
            matchingOption &&
            normalizeColor(matchingOption.value) === normalizeColor(requiredValue)
          );
        });
      }) || null
    );
  }

  function findParentVariant(variants, selections) {
    if (!Array.isArray(variants) || !Array.isArray(selections)) return null;
    return (
      variants.find(
        (variant) =>
          Array.isArray(variant.options) &&
          variant.options.length === selections.length &&
          variant.options.every((option, index) => option === selections[index]),
      ) || null
    );
  }

  function isOptionValueAvailable(variants, selections, optionIndex, value) {
    if (!Array.isArray(variants) || !Array.isArray(selections)) return false;
    return variants.some((variant) => {
      if (!variant.available || !Array.isArray(variant.options)) return false;
      if (variant.options[optionIndex] !== value) return false;
      for (let index = 0; index < optionIndex; index += 1) {
        if (variant.options[index] !== selections[index]) return false;
      }
      return true;
    });
  }

  function applyComponentImage(image, variant) {
    if (!image || !variant || !variant.image) return false;
    image.src = variant.image;
    image.alt = variant.imageAlt || variant.title || '';
    image.removeAttribute('srcset');
    image.removeAttribute('sizes');
    return true;
  }

  function buildLocalizedProductUrl(productUrl, currentUrl, localeRoot, parameters = {}) {
    const current = new URL(currentUrl);
    const request = new URL(productUrl, current.origin);
    const localePath = String(localeRoot || '/')
      .replace(/^\/+|\/+$/g, '')
      .trim();
    const normalizedLocaleRoot = localePath ? `/${localePath}/` : '/';

    if (normalizedLocaleRoot !== '/' && !request.pathname.startsWith(normalizedLocaleRoot)) {
      request.pathname = `${normalizedLocaleRoot}${request.pathname.replace(/^\/+/, '')}`;
    }

    request.searchParams.delete('variant');
    request.searchParams.delete('section_id');
    current.searchParams.forEach((value, key) => {
      if (key === 'variant' || key === 'section_id') return;
      request.searchParams.set(key, value);
    });

    Object.entries(parameters).forEach(([key, value]) => {
      if (value === undefined || value === null || value === '') return;
      request.searchParams.set(key, String(value));
    });

    return `${request.pathname}${request.search}`;
  }

  global.GeometricGoodsBundleBuilder = {
    normalizeColor,
    findComponentVariant,
    findParentVariant,
    isOptionValueAvailable,
    applyComponentImage,
    buildLocalizedProductUrl,
  };

  if (!global.document || !global.customElements || !global.HTMLElement) return;

  class BundleBuilderElement extends global.HTMLElement {
    connectedCallback() {
      this.variantPicker = this.querySelector('variant-radios');
      this.parentVariants = this.readJson(
        this.variantPicker && this.variantPicker.querySelector('[data-bundle-parent-variants]'),
      );
      this.handleChange = this.handleChange.bind(this);
      this.addEventListener('change', this.handleChange);
      this.sync();

      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribeVariantChange = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          if (!event || !event.data || event.data.sectionId !== this.dataset.section) return;
          this.syncAvailability();
          this.syncStatus();
          this.enforceSubmitLabel(event.data.variant);
        });
      }
    }

    disconnectedCallback() {
      this.removeEventListener('change', this.handleChange);
      if (typeof this.unsubscribeVariantChange === 'function') this.unsubscribeVariantChange();
    }

    readJson(script) {
      if (!script) return [];
      try {
        const value = JSON.parse(script.textContent);
        return Array.isArray(value) ? value : [];
      } catch (error) {
        return [];
      }
    }

    getSelections() {
      if (!this.variantPicker) return [];
      return Array.from(this.variantPicker.querySelectorAll('fieldset')).map((fieldset) => {
        const checked = fieldset.querySelector('input[type="radio"]:checked');
        return checked ? checked.value : '';
      });
    }

    handleChange(event) {
      const input = event.target.closest('input[type="radio"]');
      if (!input || !this.contains(input)) return;
      this.syncCard(input.closest('[data-bundle-component-card]'));
      this.syncAvailability();
      this.syncStatus();
      this.enforceSubmitLabel();
      this.syncLocalizationReturnPaths();
    }

    syncLocalizationReturnPaths() {
      const returnPath = global.location.pathname + global.location.search + global.location.hash;
      global.document
        .querySelectorAll('form[action$="/localization"] input[name="return_to"]')
        .forEach((input) => {
          input.value = returnPath;
        });
    }

    sync() {
      this.querySelectorAll('[data-bundle-component-card]').forEach((card) => {
        this.syncCard(card);
      });
      this.syncAvailability();
      this.syncStatus();
      this.enforceSubmitLabel();
    }

    syncCard(card) {
      if (!card) return;
      const primaryOption = card.querySelector('[data-bundle-primary-option]');
      const primaryInput = primaryOption && primaryOption.querySelector('input[type="radio"]:checked');
      if (!primaryInput) return;
      const color = primaryInput.value;
      const selectedValue = card.querySelector('[data-bundle-selected-value]');
      if (selectedValue) selectedValue.textContent = color;

      const variants = this.readJson(card.querySelector('[data-bundle-component-variants]'));
      const image = card.querySelector('[data-bundle-component-image]');
      const fixedOptions = {};
      if (card.dataset.fixedOptionName && card.dataset.fixedOptionValue) {
        fixedOptions[card.dataset.fixedOptionName] = card.dataset.fixedOptionValue;
      }
      card.querySelectorAll('[data-bundle-secondary-option]').forEach((fieldset) => {
        const checked = fieldset.querySelector('input[type="radio"]:checked');
        if (!checked || !card.dataset.secondaryVariantOptionName) return;
        fixedOptions[card.dataset.secondaryVariantOptionName] = checked.value;
        const selectedSecondaryValue = fieldset.querySelector(
          '[data-bundle-secondary-selected-value]',
        );
        if (selectedSecondaryValue) selectedSecondaryValue.textContent = checked.value;
      });
      applyComponentImage(
        image,
        findComponentVariant(variants, color, card.dataset.variantOptionName, fixedOptions),
      );
    }

    syncAvailability() {
      if (!this.variantPicker) return;
      const selections = this.getSelections();
      this.variantPicker.querySelectorAll('fieldset').forEach((fieldset, optionIndex) => {
        fieldset.querySelectorAll('input[type="radio"]').forEach((input) => {
          const available = isOptionValueAvailable(
            this.parentVariants,
            selections,
            optionIndex,
            input.value,
          );
          input.disabled = !available;
          const label = fieldset.querySelector('label[for="' + input.id + '"]');
          if (!label) return;
          label.classList.toggle('is-disabled', !available);
          label.setAttribute('aria-disabled', String(!available));
        });
      });
    }

    syncStatus() {
      const liveRegion = this.querySelector('[data-bundle-availability]');
      if (!liveRegion) return;
      const variant = findParentVariant(this.parentVariants, this.getSelections());
      const available = Boolean(variant && variant.available);
      liveRegion.textContent = available ? '' : this.dataset.selectionUnavailable;
      liveRegion.toggleAttribute('hidden', available);
    }

    enforceSubmitLabel(variant) {
      const selectedVariant = variant || findParentVariant(this.parentVariants, this.getSelections());
      if (!selectedVariant || !selectedVariant.available) return;
      const productForm = global.document.getElementById(this.dataset.formId);
      const submitText = productForm && productForm.querySelector('[name="add"] > span');
      if (submitText) submitText.textContent = this.dataset.submitLabel;
    }
  }

  if (!global.customElements.get('bundle-builder')) {
    global.customElements.define('bundle-builder', BundleBuilderElement);
  }
})(globalThis);
